import { createContext, useContext, useEffect, useLayoutEffect, useRef, useState, type ReactElement, type CSSProperties } from 'react'
import { motion } from 'framer-motion'
import type { BattleResult, UnitRef } from '@origami/shared'
import {
  GLYPHS,
  Petal,
  TieredCritter,
  HeartStatImg,
  StrengthStatImg,
  ELEMENT_INFO,
  ANIMAL_INFO,
} from '../design-system'
import type { GlyphKey, Star } from '@origami/shared'
import { useReplay } from '../replay/useReplay'
import type {
  BattleDisplayState,
  FloatingNumber,
  ImpactBurst,
  LogEntry,
  ReplayControls,
  ReplayProgress,
  UnitDisplayState,
} from '../replay/types'
import { demoBattle, demoBattleMeta } from '../replay/fixtures/demoBattle'
import { realBattle, realBattleMeta } from '../replay/fixtures/realBattle'
import { useIsMobile, useIsFoldOpen, RotatePrompt } from './useIsMobile'
import { useCountdown } from './useCountdown'

/** Toggle the live-sim fixture via /?dombattle=real (legacy DOM
 *  renderer route; the old /?battle now serves the Pixi page). */
function pickFixture() {
  if (typeof window !== 'undefined') {
    const params = new URLSearchParams(window.location.search)
    const which = params.get('dombattle') ?? params.get('battle')
    if (which === 'real') {
      return { battle: realBattle, meta: realBattleMeta }
    }
  }
  return { battle: demoBattle, meta: demoBattleMeta }
}

type Side = 'A' | 'B'

/** Meta describing the surrounding context of a battle (player
 *  identities, seed, round, per-unit glyph hints). Shape pulled from
 *  the fixtures; SoloPlayScreen builds a matching object from the
 *  player profile + the /ready response. */
export type BattleMeta = typeof demoBattleMeta

const MetaContext = createContext<BattleMeta | null>(null)
function useMeta(): BattleMeta {
  const m = useContext(MetaContext)
  if (!m) throw new Error('BattleScreen children must be wrapped in MetaContext')
  return m
}

/** Provider wrapper so alternate battle pages (PixiBattleScreen) can
 *  reuse the exported chrome components below without this module
 *  exposing the raw context object. */
export function BattleMetaProvider({
  meta,
  children,
}: {
  meta: BattleMeta
  children: React.ReactNode
}): ReactElement {
  return <MetaContext.Provider value={meta}>{children}</MetaContext.Provider>
}

interface BattleScreenProps {
  /** When omitted, defaults to the fixture chosen by the URL
   *  (pickFixture). SoloPlayScreen passes the live BattleResult from
   *  POST /api/solo/ready. */
  battle?: BattleResult
  /** When omitted, defaults alongside `battle` from the fixture. */
  meta?: BattleMeta
  /** Renders a "back to shop" affordance when provided. SoloPlayScreen
   *  passes the dismissBattle action so the player can return to the
   *  shop after watching the replay. */
  onExit?: () => void
}

/* ============================================================
 * Top-level screen — paper theater architecture
 * ============================================================ */

/** Toggle for the centered winner modal. Disabled while the
 *  in-stage WinnerBanner hanko stamp carries the win announcement.
 *  Typed as `boolean` (not `false`) so TS still narrows
 *  `state.winner` inside the JSX conditional below. */
const SHOW_WINNER_MODAL: boolean = false

export function BattleScreen({ battle, meta, onExit }: BattleScreenProps = {}): ReactElement {
  const fixture = battle && meta ? null : pickFixture()
  const activeBattle = battle ?? fixture!.battle
  const activeMeta: BattleMeta = meta ?? (fixture!.meta as BattleMeta)
  const { state, progress, controls } = useReplay(activeBattle, {
    turnBudgetMs: 2800,
    initialSpeed: 1,
    autoplay: true,
  })
  const isMobile = useIsMobile()
  const foldOpen = useIsFoldOpen()

  return (
    <MetaContext.Provider value={activeMeta}>
    <div className={`paper bt-page${isMobile ? ' bt-page--mobile' : ''}${foldOpen ? ' bt-page--fold' : ''}`}>
      {/* Desktop renders the floating exit button (fixed at top-left).
          The mobile + Fold layouts instead bake the exit affordance
          into the header strip so we don't have to reserve a 36px
          no-go zone at the top of the page for a fixed overlay. */}
      {!isMobile && !foldOpen && onExit && (
        <button
          type="button"
          className="bt-exit"
          onClick={onExit}
          aria-label="Return to shop"
        >
          ↩ shop
        </button>
      )}
      {foldOpen ? (
        <FoldLayout state={state} progress={progress} controls={controls} onExit={onExit} />
      ) : isMobile ? (
        <MobileLayout state={state} progress={progress} controls={controls} onExit={onExit} />
      ) : (
        <>
          {/* Ambient petals — drifted across the stage so the theater
              feels situated in a garden rather than floating in a void.
              Skipped on mobile to keep the small viewport calm. */}
          <div className="petal" style={{ top: '12vh', right: '8vw' }}>
            <Petal size={22} />
          </div>
          <div className="petal" style={{ top: '34vh', left: '6vw', animationDelay: '1.1s' }}>
            <Petal size={18} color="#ffd166" />
          </div>
          <div className="petal" style={{ top: '64vh', right: '11vw', animationDelay: '2.4s' }}>
            <Petal size={16} color="#c9b4ff" />
          </div>
          <div className="petal" style={{ top: '80vh', left: '14vw', animationDelay: '0.6s' }}>
            <Petal size={20} />
          </div>

          <div className="bt-shell">
            <BrandRibbon progress={progress} />
            <Theater state={state} />
            <LacquerConsole progress={progress} controls={controls} />
            <Chronicle entries={state.log} state={state} />
          </div>
        </>
      )}

      {SHOW_WINNER_MODAL && state.finished && state.winner && (
        <WinnerOverlay winner={state.winner} onRestart={controls.restart} />
      )}
    </div>
    </MetaContext.Provider>
  )
}

/* ============================================================
 * 1. Brand ribbon — kanji wordmark + ceremonial chips
 * ============================================================ */

export function BrandRibbon({ progress }: { progress: ReplayProgress }): ReactElement {
  const meta = useMeta()
  const { round, pairing, seed, playerA, playerB, nextRoundAtMs } = meta
  const intermissionRemaining = useCountdown(nextRoundAtMs ?? null)
  return (
    <header className="bt-ribbon">
      <div className="bt-brand">
        {/* 折紙対戦 — origami battle / face-off */}
        <div className="bt-brand__kanji" aria-label="Origami Autobattler">
          折紙
          <br />
          対戦
        </div>
        <div className="bt-brand__rule" aria-hidden="true" />
        <div className="bt-brand__meta">
          <span className="bt-brand__series">
            replay theater · round {round.toString().padStart(2, '0')} · pairing {pairing.toString().padStart(2, '0')}
          </span>
          <span className="bt-brand__title">
            {playerA.name} vs. {playerB.name}
            <span className="bt-brand__seal">朱</span>
          </span>
        </div>
      </div>
      <div className="bt-ribbon__chips" aria-label="battle metadata">
        <span className="bt-ribbon__chip bt-ribbon__chip--turn">
          <span className="bt-ribbon__chip-icon" /> turn {progress.currentTurn} / {progress.totalTurns}
        </span>
        <span className="bt-ribbon__chip bt-ribbon__chip--seed">
          <span className="bt-ribbon__chip-icon" /> seed {seed}
        </span>
        {nextRoundAtMs != null && (
          <span className="bt-ribbon__chip bt-ribbon__chip--intermission">
            <span className="bt-ribbon__chip-icon" /> next round · {intermissionRemaining}s
          </span>
        )}
      </div>
    </header>
  )
}

/* ============================================================
 * 2. Theater — two scrolls flanking the wide stage
 * ============================================================ */

function Theater({ state }: { state: BattleDisplayState }): ReactElement {
  return (
    <div className="bt-theater">
      <PlayerScroll side="A" state={state} />
      <Stage state={state} />
      <PlayerScroll side="B" state={state} />
    </div>
  )
}

/* ------------------------------------------------------------
 * 2a. Kakejiku player scroll
 * ------------------------------------------------------------ */

export function PlayerScroll({ side, state }: { side: Side; state: BattleDisplayState }): ReactElement {
  const meta = useMeta()
  const player = side === 'A' ? meta.playerA : meta.playerB
  const synergies = state.teams[side].synergies
  const Mascot = GLYPHS[player.mascot]!
  const hpPct = (player.hp / player.maxHp) * 100
  const low = hpPct < 33

  return (
    <aside className={`bt-scroll bt-scroll--${side}`} aria-label={`${player.name} scroll`}>
      {/* Painted ScrollBanner-Blue / -Red PNG carries the hardware
          and patterned chiyogami border as a single image (applied
          via .bt-scroll--A/B background-image). Body is inset into
          the cream center region of the painting. */}
      <div className="bt-scroll__body">
        <span className="bt-scroll__record" title="match record">{player.record}</span>
        <div className="bt-scroll__header">
          <div className="bt-scroll__hanko">
            <div style={{ transform: 'scale(0.72)' }}>
              <Mascot size={80} />
            </div>
          </div>
          <div className="bt-scroll__name">{player.name}</div>
          <div className="bt-scroll__tag">{player.tag}</div>
        </div>

        <div className="bt-scroll__hp" aria-label="player HP">
          <span className="bt-scroll__hp-readout">
            ♥ {player.hp}/{player.maxHp}
          </span>
          <div className="bt-scroll__hp-bar">
            <span
              className={`bt-scroll__hp-fill${low ? ' bt-scroll__hp-fill--low' : ''}`}
              style={{ width: `${hpPct}%` }}
            />
          </div>
        </div>

        <div className="bt-scroll__seals" aria-label="active synergies">
          {synergies.length === 0 && (
            <span className="bt-scroll__seals-empty">(no synergies)</span>
          )}
          {synergies.map((s) => (
            <span
              key={`${s.key}-${s.threshold}`}
              className="bt-scroll__seal synergy-flash"
              title={s.description}
            >
              <span className="bt-scroll__seal-mark" />
              {s.key} ×{s.threshold}
            </span>
          ))}
        </div>
      </div>
    </aside>
  )
}

/* ------------------------------------------------------------
 * 2b. Stage — wide battlefield with horizon, mountains, floor
 * ------------------------------------------------------------ */

function Stage({ state }: { state: BattleDisplayState }): ReactElement {
  // Both rails use `flex-direction: row` so visual order = DOM order.
  // Team A: front unit (lowest displayPosition) belongs at the RIGHT
  //   edge of its rail (closest to the midline) — reverse the array
  //   so the front renders last, then justify-content: flex-end packs
  //   the cluster against the right edge.
  // Team B: front unit belongs at the LEFT edge of its rail (closest
  //   to the midline). Ascending order + justify-content: flex-start
  //   does that naturally.
  const meta = useMeta()
  const teamA = sortByDisplay(state.order.A.map((r) => state.units[r]).filter(Boolean) as UnitDisplayState[]).reverse()
  const teamB = sortByDisplay(state.order.B.map((r) => state.units[r]).filter(Boolean) as UnitDisplayState[])
  return (
    <div className="bt-stage">
      <div className="bt-stage__mountains" aria-hidden="true" />
      <div className="bt-stage__horizon" aria-hidden="true" />
      <div className="bt-stage__valley" aria-hidden="true" />
      <div className="bt-stage__bell" aria-hidden="true">
        <div className="bt-stage__bell-petal" />
        <div className="bt-stage__bell-bead" />
      </div>

      <span className="bt-stage__label bt-stage__label--A">
        ← {meta.playerA.name}{meta.viewerSide === 'A' ? ' · you' : ''}
      </span>
      <span className="bt-stage__label bt-stage__label--B">
        {meta.playerB.name}{meta.viewerSide === 'B' ? ' · you' : ''} →
      </span>

      <div className="bt-stage__rail bt-stage__rail--A">
        {teamA.map((u) => <BattleUnit key={u.ref} unit={u} side="A" />)}
      </div>
      <div className="bt-stage__rail bt-stage__rail--B">
        {teamB.map((u) => <BattleUnit key={u.ref} unit={u} side="B" />)}
      </div>

      <FloatingNumberLayer floats={state.floatingNumbers} units={state.units} order={state.order} />
      <ImpactBurstLayer bursts={state.impactBursts} units={state.units} order={state.order} />

      {state.finished && state.winner && (
        <WinnerBanner winner={state.winner} />
      )}
    </div>
  )
}

/* ------------------------------------------------------------
 * 2d. WinnerBanner — large hanko-stamp announcing the victor.
 *     Sits in the sky portion of the stage on the winning team's
 *     side (left half for A, right half for B, centered for a tie).
 *     Reuses the codex stamp vocabulary (mono caps, tinted border,
 *     rotation, slight transparency) but scaled up dramatically
 *     with a kanji headline and a drop-stamp entry animation.
 * ------------------------------------------------------------ */

export function WinnerBanner({ winner }: { winner: 'A' | 'B' | 'tie' }): ReactElement {
  const meta = useMeta()
  const kanji = winner === 'A' || winner === 'B' ? '勝' : '引'
  const caption = winner === 'tie' ? 'a tie' : 'winner'
  const team =
    winner === 'A'   ? `${meta.playerA.name} · 折` :
    winner === 'B'   ? `${meta.playerB.name} · 折` :
                       '— · 引き分け'
  return (
    <div className={`bt-winner-banner bt-winner-banner--${winner}`} aria-hidden="true">
      <div className="bt-winner-banner__plate">
        <span className="bt-winner-banner__kanji">{kanji}</span>
        <span className="bt-winner-banner__divider" />
        <span className="bt-winner-banner__caption">{caption}</span>
        <span className="bt-winner-banner__team">{team}</span>
      </div>
    </div>
  )
}

function sortByDisplay(units: UnitDisplayState[]): UnitDisplayState[] {
  return [...units].sort((a, b) => {
    if (a.visualState === 'gone' && b.visualState !== 'gone') return 1
    if (b.visualState === 'gone' && a.visualState !== 'gone') return -1
    return a.displayPosition - b.displayPosition
  })
}

/* ------------------------------------------------------------
 * 2c. Battle unit — same animation hooks as before, freshened
 *     wrapper that integrates with the stage's floor shadow.
 * ------------------------------------------------------------ */

function BattleUnit({ unit, side, compact = false }: { unit: UnitDisplayState; side: Side; compact?: boolean }): ReactElement {
  // Resolve to a glyph key with tiered bitmap art if available
  // (TieredCritter falls back to the SVG silhouette via UnitGlyph if
  // no tier-N PNG is registered for the key).
  const meta = useMeta()
  const glyphKey = (unit.glyph ?? meta.unitGlyphs[unit.unit_id] ?? 'crane') as GlyphKey
  const star = (unit.star as Star) ?? 1
  const hpPct = unit.maxHp > 0 ? Math.max(0, (unit.hp / unit.maxHp) * 100) : 0
  const isLow = hpPct < 33 && unit.visualState !== 'gone' && unit.visualState !== 'death'
  const isGone = unit.visualState === 'gone'

  // Per-unit battle render hints from UnitDef (plumbed via BattleMeta).
  // body_offset_y: positive = pixels lifted UP (translateY negative in CSS).
  // is_flying:     additional FLYING_LIFT_PX lift + +10° forward strike tilt.
  // body_scale:    multiplier on the body (1.0 default; <1 shrinks tiny
  //                units, >1 grows oversized capstones).
  const isFlying = meta.unitFlying[unit.unit_id] === true
  const bodyOffsetY = meta.unitBodyOffsetY[unit.unit_id] ?? 0
  const bodyScale = meta.unitBodyScale[unit.unit_id] ?? 1
  const FLYING_LIFT_PX = 28
  const totalLiftPx = bodyOffsetY + (isFlying ? FLYING_LIFT_PX : 0)

  // FLIP slide tween — gated on displayPosition change so the tween
  // only fires when slide_forward actually re-indexed the lineup,
  // NOT on every re-render. Otherwise idle units slide a few pixels
  // every time a neighbor's HP changes or a floating number spawns
  // (subpixel layout shifts trigger the tween spuriously).
  //
  // We still sample the rect each render so the comparison has a
  // valid "before" snapshot for the moment displayPosition changes.
  const wrapperRef = useRef<HTMLDivElement>(null)
  const lastRectRef = useRef<DOMRect | null>(null)
  const lastDisplayPosRef = useRef<number>(unit.displayPosition)
  useLayoutEffect(() => {
    const el = wrapperRef.current
    if (!el) return
    if (isGone) {
      lastRectRef.current = null
      lastDisplayPosRef.current = unit.displayPosition
      return
    }
    const newRect = el.getBoundingClientRect()
    const oldRect = lastRectRef.current
    const posChanged = unit.displayPosition !== lastDisplayPosRef.current
    if (posChanged && oldRect && oldRect.width > 0) {
      const dx = oldRect.left - newRect.left
      if (Math.abs(dx) >= 1) {
        el.style.transition = 'none'
        el.style.transform = `translateX(${dx}px)`
        void el.offsetWidth
        el.style.transition = 'transform 0.45s cubic-bezier(0.4, 0, 0.2, 1)'
        el.style.transform = ''
      }
    }
    lastRectRef.current = newRect
    lastDisplayPosRef.current = unit.displayPosition
  })

  // Strike picks the --flying keyframe variant for airborne units so
  // the lunge reads as a diving arc instead of a ground charge.
  // Windup stays uniform — only the strike phase varies.
  const strikeClass =
    side === 'A'
      ? (isFlying ? 'attack-strike-l--flying' : 'attack-strike-l')
      : (isFlying ? 'attack-strike-r--flying' : 'attack-strike-r')
  const outerAnimClass =
    unit.visualState === 'attack_windup'  ? (side === 'A' ? 'attack-windup-l' : 'attack-windup-r') :
    unit.visualState === 'attack_release' ? strikeClass :
    unit.visualState === 'take_hit'       ? 'take-hit' :
    ''

  // Idle animation forks on flight: flying units `.bob` (gentle
  // vertical hover that reads as wingbeat float); ground units
  // `.wiggle` (small left/right sway, no vertical motion that would
  // conflict with the standing-on-the-floor feel).
  const idleAnim = isFlying ? 'bob' : 'wiggle'
  const baseInnerClass =
    unit.visualState === 'death'        ? 'death-fold' :
    unit.visualState === 'cast_ability' ? 'cast-glow' :
    unit.visualState === 'victory'      ? 'victory-bounce' :
    unit.visualState === 'summoning'    ? 'unfold-in' :
    unit.visualState === 'idle'         ? idleAnim :
    ''
  const pulseClass =
    unit.pulseEffect === 'healed'       ? 'healed' :
    unit.pulseEffect === 'buffed_up'    ? 'buffed-up' :
    unit.pulseEffect === 'buffed_down'  ? 'buffed-down' :
    unit.pulseEffect === 'mitigated'    ? 'mitigated' :
    ''
  const innerAnimClass = pulseClass ? `${baseInnerClass} ${pulseClass}`.trim() : baseInnerClass

  return (
    <div
      ref={wrapperRef}
      data-ref={unit.ref}
      className="bt-unit"
      style={{ display: isGone ? 'none' : 'flex' }}
    >
      <div className="bt-unit__statline">
        <div className="bt-unit__hp-bar">
          <span
            className={`bt-unit__hp-fill${hpPct < 30 ? ' bt-unit__hp-fill--low' : ''}`}
            style={{ width: `${hpPct}%` }}
          />
        </div>
      </div>

      <div className={`bt-unit__glyph ${outerAnimClass}`}>
        {/* Floor shadow sits OUTSIDE .bt-unit__face so the flying
            lift (translateY on the face) doesn't drag the shadow
            up into the air with the body — cast shadow stays on
            the ground while the unit hovers. Still inside
            .bt-unit__glyph so the strike animation moves the shadow
            with the lunge (consistent with ground-unit behavior). */}
        <div className="bt-unit__shadow" aria-hidden="true" />
        {/* Flip wrapper carries scaleX(-1) for Team B independently
            of the outer animation. Without this, attack-windup /
            attack-strike / take-hit keyframes (which override the
            `transform` property) wipe the flip mid-animation and the
            unit briefly faces the wrong way. */}
        <div
          className="bt-unit__face"
          style={{
            // Combine the team-B mirror flip with the per-unit vertical
            // lift (body_offset_y + flying lift) and body_scale. All
            // live on the same wrapper because the OUTER
            // `.bt-unit__glyph` gets its transform clobbered by the
            // strike-animation keyframes (per CLAUDE.md "CSS animation
            // overrides inline transform" gotcha — same reason the
            // scaleX flip is already on this inner wrapper).
            //
            // Order matters: translateY FIRST then scale, so the
            // pixel-lift stays in screen coords regardless of scale.
            // scaleX(-1) goes last so the mirror applies in the
            // already-scaled local space (no surprises with negative
            // x multiplied by per-unit scale).
            transform: `${side === 'B' ? 'scaleX(-1) ' : ''}translateY(${-totalLiftPx}px) scale(${bodyScale})`,
          }}
        >
          <div
            className={innerAnimClass}
            style={
              unit.visualState === 'cast_ability'
                ? ({ ['--cast-glow-rgb' as unknown as keyof CSSProperties]: ELEMENT_RGB[meta.unitElements?.[unit.unit_id] ?? ''] ?? '255, 107, 74' } as CSSProperties)
                : undefined
            }
          >
            <TieredCritter glyph={glyphKey} star={star} size={72} unitId={unit.unit_id} />
          </div>
          {unit.visualState === 'death' && (
            <img className="death-ball" src="/glyph-assets/crumpled-ball.png" alt="" />
          )}
          {unit.visualState === 'summoning' && (
            <>
              <img className="unfold-stage-flat"  src="/glyph-assets/unfold-flat.png"  alt="" />
              <img className="unfold-stage-fold1" src="/glyph-assets/unfold-fold1.png" alt="" />
              <img className="unfold-stage-fold2" src="/glyph-assets/unfold-fold2.png" alt="" />
              <img className="unfold-stage-fold3" src="/glyph-assets/unfold-fold3.png" alt="" />
            </>
          )}
          {isLow && (
            <div
              className="bt-unit__low-alert pop"
              style={{ transform: `translateX(-50%) ${side === 'B' ? 'scaleX(-1)' : ''}` }}
            >
              !
            </div>
          )}
        </div>
      </div>

      <div className="bt-unit__readouts">
        {/* HeartStatImg / StrengthStatImg set their outer div's
            width/height via inline style — CSS can't override those
            without !important. Use the `compact` flag to size the
            stat readouts for mobile rails (57px-wide unit wrapper —
            two 26px readouts + 3px gap = 55px, fits with 2px of
            breathing room). Sized at 26 alongside the rail glyph
            at 52 so mobile/Fold reads ~30% larger than the
            original 20/40 baseline. */}
        <HeartStatImg value={Math.max(0, unit.hp)} size={compact ? 26 : 38} />
        <StrengthStatImg value={unit.atk} size={compact ? 26 : 38} />
      </div>

      {unit.statuses.length > 0 && (
        <div className="bt-unit__statuses">
          {unit.statuses.map((s) => (
            <span key={s} className="bt-unit__status">{s}</span>
          ))}
        </div>
      )}
    </div>
  )
}

/* ============================================================
 * 3. Floating numbers + impact bursts
 *    (positioning logic copied from the previous implementation
 *    so the math still aligns with the new stage rails)
 * ============================================================ */

function slotLeftPct(units: Record<UnitRef, UnitDisplayState>, order: { A: UnitRef[]; B: UnitRef[] }, anchor: UnitRef): number | null {
  const unit = units[anchor]
  if (!unit) return null
  const sideRefs = order[unit.team]
  const siblings = sideRefs
    .map((r) => units[r])
    .filter((u): u is UnitDisplayState => Boolean(u && u.visualState !== 'gone'))
    .sort((a, b) => a.displayPosition - b.displayPosition)
  const sideIdx = siblings.findIndex((u) => u.ref === anchor)
  if (sideIdx < 0) return null
  // Approximate horizontal % per slot from the rail-anchored layout
  // (must stay in sync with .bt-stage__rail metrics in
  // battle-screen.css):
  //   - rail width = 47% of stage, padding 16px each side
  //   - unit wrapper = 84px wide, gap = 10px between units
  // On a typical ~1040px stage:
  //   front unit center ≈ rail right edge (50%) − 16px padding −
  //     42px half-width ≈ 44.4% → 5.6% offset from stage center.
  //   each step back adds (84 + 10) / 1040 ≈ 9.0% offset.
  // Bumping `step` slightly above the analytic value (0.090 → 0.094)
  // catches narrower viewports where the unit wrapper shrinks via
  // flex; the float still lands inside the glyph box visually.
  const slotPct = 0.056 + sideIdx * 0.094
  return unit.team === 'A' ? (0.5 - slotPct) * 100 : (0.5 + slotPct) * 100
}

function FloatingNumberLayer({
  floats,
  units,
  order,
}: {
  floats: FloatingNumber[]
  units: Record<UnitRef, UnitDisplayState>
  order: { A: UnitRef[]; B: UnitRef[] }
}): ReactElement {
  return (
    <>
      {floats.map((fn) => {
        const left = slotLeftPct(units, order, fn.anchor)
        if (left === null) return null
        // Synergy-granted bonuses color in their synergy's element /
        // animal accent so each +N's origin reads at a glance.
        const synergyColor = fn.synergyKey
          ? fn.synergyKey in ELEMENT_INFO
            ? `var(--el-${fn.synergyKey}-deep)`
            : ANIMAL_INFO[fn.synergyKey as keyof typeof ANIMAL_INFO]?.accent
          : undefined
        const color =
          synergyColor ??
          (fn.variant === 'damage-up'   ? 'var(--vermillion-deep)' :
          fn.variant === 'damage'      ? 'var(--vermillion)' :
          fn.variant === 'damage-down' ? 'var(--ink-fade)' :
          fn.variant === 'heal'        ? 'var(--mint-deep)' :
          fn.variant === 'buff'        ? 'var(--gold-deep)' :
          fn.variant === 'debuff'      ? 'var(--lavender-deep)' :
          'var(--ink)')
        return (
          <div
            key={fn.id}
            className="float-number"
            style={{
              left: `${left}%`,
              top: '55%',
              // Pixel font (Jersey 15) so the damage / heal / buff
              // numbers visually match the bitmap HP and STR icons.
              fontFamily: 'var(--pixel)',
              fontSize: fn.variant.startsWith('damage') ? 38 : 28,
              color,
              WebkitTextStroke: '2px var(--ink)',
              textShadow: '0 3px 0 var(--ink)',
              fontWeight: 400,
              letterSpacing: '0.04em',
              lineHeight: 1,
            }}
          >
            {fn.icon && <span style={{ fontSize: '0.7em', marginRight: 4 }}>{fn.icon}</span>}
            {fn.text}
          </div>
        )
      })}
    </>
  )
}

/** Element → RGB triple for `--proj-rgb` on projectile / impact
 *  bursts. Values mirror the deep-element CSS vars in tokens.css
 *  (visually distinguishable in the stage's cream palette). */
const ELEMENT_RGB: Record<string, string> = {
  fire:  '196, 64, 48',     // vermillion-deep
  water: '38, 78, 124',     // indigo-deep
  earth: '160, 110, 60',    // ochre-deep
  wood:  '70, 130, 80',     // sage-deep
  wind:  '170, 140, 200',   // lavender-deep
}

function resolveProjRgb(units: Record<UnitRef, UnitDisplayState>, meta: BattleMeta, sourceRef?: UnitRef): string {
  if (!sourceRef) return '232, 198, 110' // generic gold
  const u = units[sourceRef]
  if (!u) return '232, 198, 110'
  const el = meta.unitElements?.[u.unit_id]
  return (el && ELEMENT_RGB[el]) || '232, 198, 110'
}

function ImpactBurstLayer({
  bursts,
  units,
  order,
}: {
  bursts: ImpactBurst[]
  units: Record<UnitRef, UnitDisplayState>
  order: { A: UnitRef[]; B: UnitRef[] }
}): ReactElement {
  const meta = useMeta()
  return (
    <>
      {bursts.map((b) => {
        // Synergy banner — midpoint label, slightly above the strike row.
        if (b.kind === 'synergy_tag') {
          const teamColor =
            b.team === 'A' ? 'rgb(38, 78, 124)' :
            b.team === 'B' ? 'rgb(196, 64, 48)' :
            'var(--ink)'
          return (
            <div
              key={b.id}
              className="synergy-tag"
              style={{ left: '50%', top: '38%', ['--syn-tag-color' as unknown as keyof CSSProperties]: teamColor } as CSSProperties}
            >
              {b.kanji && <span className="synergy-tag__kanji">{b.kanji}</span>}
              {b.label}
            </div>
          )
        }
        // Projectile — Framer Motion animates `left` from source to
        // target slot; the keyframe handles the vertical arc + scale.
        if (b.kind === 'projectile') {
          const fromLeft = b.source ? slotLeftPct(units, order, b.source) : null
          const toLeft = b.anchor !== 'midpoint' ? slotLeftPct(units, order, b.anchor) : null
          if (fromLeft === null || toLeft === null) return null
          const rgb = resolveProjRgb(units, meta, b.source)
          return (
            <motion.div
              key={b.id}
              className="projectile"
              initial={{ left: `${fromLeft}%`, top: '61%' }}
              animate={{ left: `${toLeft}%` }}
              transition={{ duration: 0.34, ease: 'easeInOut' }}
              style={{ ['--proj-rgb' as unknown as keyof CSSProperties]: rgb } as CSSProperties}
            />
          )
        }
        // Ability impact — paper-fold splash at the target.
        if (b.kind === 'ability_impact') {
          if (b.anchor === 'midpoint') return null
          const left = slotLeftPct(units, order, b.anchor)
          if (left === null) return null
          const rgb = resolveProjRgb(units, meta, b.source)
          return (
            <div
              key={b.id}
              className="ability-impact"
              style={{ left: `${left}%`, top: '61%', ['--proj-rgb' as unknown as keyof CSSProperties]: rgb } as CSSProperties}
            />
          )
        }
        if (b.anchor === 'midpoint') {
          return (
            <div key={b.id} className="clash-burst" style={{ left: '50%', top: '61%' }} />
          )
        }
        const left = slotLeftPct(units, order, b.anchor)
        if (left === null) return null
        const className = b.kind === 'super' ? 'super-burst' : 'resist-burst'
        return <div key={b.id} className={className} style={{ left: `${left}%`, top: '61%' }} />
      })}
    </>
  )
}

/* ============================================================
 * 4. Chronicle — horizontal manuscript event log
 * ============================================================ */

export function Chronicle({
  entries,
  state,
}: {
  entries: LogEntry[]
  state: BattleDisplayState
}): ReactElement {
  const meta = useMeta()
  // Vertical log — newest entries at the TOP, older entries push
  // downward and eventually fade off the bottom via the scroll
  // container's mask gradient.
  return (
    <section className="bt-chronicle" aria-label="battle chronicle">
      <header className="bt-chronicle__caption">
        <span className="bt-chronicle__caption-kanji">戦記</span>
        <span className="bt-chronicle__caption-roman">chronicle</span>
        <span className="bt-chronicle__live-dot">live</span>
      </header>
      <div className="bt-chronicle__scroll" role="log" aria-live="polite">
        {entries.length === 0 && (
          <span className="bt-chronicle__empty">battle starting…</span>
        )}
        {entries.map((e) => (
          <article key={e.id} className="bt-chronicle__entry">
            <span className="bt-chronicle__entry-turn">
              T{String(e.turn).padStart(2, '0')}
            </span>
            <span className="bt-chronicle__entry-icon" style={{ color: e.accent }}>{e.icon}</span>
            <span className="bt-chronicle__entry-text" style={{ color: e.accent }}>
              {renderLogText(e.text, state, meta.unitNames)}
            </span>
          </article>
        ))}
      </div>
    </section>
  )
}

/** Split a log line on UnitRef tokens (`A:1`, `B:3`, etc.) and replace
 *  each occurrence with a team-tinted span showing the unit's display
 *  name. Falls back to the raw ref when no name is registered.
 *
 *  Why ref tokens, not pre-rendered names: log lines are emitted by
 *  the replay handler which only has the simulator's `UnitRef`s on
 *  hand. The handlers stay simple; this renderer resolves at draw
 *  time using the live `units` map + meta.unitNames catalog. */
const REF_SPLIT = /([AB]:[1-5])/g
const REF_EXACT = /^[AB]:[1-5]$/
const TEAM_A_COLOR = 'rgb(38, 78, 124)'   // --chiyo-indigo
const TEAM_B_COLOR = 'rgb(196, 64, 48)'   // --chiyo-vermillion

function renderLogText(
  text: string,
  state: BattleDisplayState,
  unitNames: Record<string, string> | undefined,
): ReactElement[] {
  // String.prototype.split with a capturing global regex puts the
  // captured tokens between the surrounding text fragments — odd
  // indices in the resulting array are the refs (when present).
  const parts = text.split(REF_SPLIT)
  return parts.map((part, i) => {
    if (!REF_EXACT.test(part)) {
      return <span key={i}>{part}</span>
    }
    const side = part[0] as 'A' | 'B'
    const unit = state.units[part as keyof typeof state.units]
    const name = (unit && unitNames?.[unit.unit_id]) || part
    return (
      <strong
        key={i}
        className={`bt-chronicle__ref bt-chronicle__ref--${side}`}
        style={{ color: side === 'A' ? TEAM_A_COLOR : TEAM_B_COLOR }}
      >
        {name}
      </strong>
    )
  })
}

/* ============================================================
 * 5. Lacquer console — playback timeline + hanko buttons
 * ============================================================ */

export function LacquerConsole({
  progress,
  controls,
}: {
  progress: ReplayProgress
  controls: ReplayControls
}): ReactElement {
  // Scrub state — while the pointer is down on the replay scroll, the
  // dot/fill/count preview the target turn; the seek commits on
  // release. Domain: position t/totalTurns for t = 0..totalTurns,
  // where t=0 is battle start (the synergy intro phase occupies the
  // segment between tick 0 and tick 1 via introFraction).
  const scrollRef = useRef<HTMLDivElement>(null)
  const [scrubTurn, setScrubTurn] = useState<number | null>(null)

  function turnFromPointer(e: React.PointerEvent): number {
    const el = scrollRef.current
    if (!el || progress.totalTurns <= 0) return 0
    const r = el.getBoundingClientRect()
    const f = Math.max(0, Math.min(1, (e.clientX - r.left) / Math.max(1, r.width)))
    return Math.round(f * progress.totalTurns)
  }

  const playheadTurn =
    scrubTurn ??
    (progress.currentTurn === 0 ? progress.introFraction : progress.currentTurn)
  const pct = progress.totalTurns > 0
    ? Math.min(100, (playheadTurn / progress.totalTurns) * 100)
    : 0
  const shownTurn = scrubTurn ?? progress.currentTurn
  const ticks = Array.from({ length: progress.totalTurns + 1 }, (_, i) =>
    progress.totalTurns > 0 ? (i / progress.totalTurns) * 100 : 0
  )
  return (
    <section className="bt-console" aria-label="replay controls">
      <div className="bt-console__group">
        <button
          type="button"
          className="bt-hanko"
          onClick={controls.stepBack}
          title="rewind one turn"
          aria-label="rewind one turn"
        >
          ⏮
        </button>
        <button
          type="button"
          className={`bt-hanko bt-hanko--play${progress.isPlaying ? ' bt-hanko--active' : ''}`}
          onClick={controls.toggle}
          title={progress.isPlaying ? 'pause' : 'play'}
          aria-label={progress.isPlaying ? 'pause' : 'play'}
        >
          {progress.isPlaying ? '❚❚' : '▶'}
        </button>
        {([0.5, 1, 2, 4] as const).map((s) => (
          <button
            key={s}
            type="button"
            className={`bt-hanko${progress.speed === s ? ' bt-hanko--active' : ''}`}
            onClick={() => controls.setSpeed(s)}
            title={`${s}× speed`}
            aria-label={`${s} times speed`}
            aria-pressed={progress.speed === s}
          >
            {s === 0.5 ? '½×' : `${s}×`}
          </button>
        ))}
      </div>

      <div
        ref={scrollRef}
        className={`bt-progress${scrubTurn !== null ? ' bt-progress--scrubbing' : ''}`}
        aria-label="turn progress — click or drag to seek"
        role="slider"
        aria-valuemin={0}
        aria-valuemax={progress.totalTurns}
        aria-valuenow={shownTurn}
        onPointerDown={(e) => {
          // Capture keeps the drag tracking when the pointer leaves
          // the bar; guarded because programmatic/synthetic pointers
          // (tests, some assistive tech) have no capturable id.
          try {
            e.currentTarget.setPointerCapture(e.pointerId)
          } catch {
            /* uncapturable pointer — drag still works over the bar */
          }
          setScrubTurn(turnFromPointer(e))
        }}
        onPointerMove={(e) => {
          if (scrubTurn !== null) setScrubTurn(turnFromPointer(e))
        }}
        onPointerUp={() => {
          if (scrubTurn !== null) {
            controls.seekToTurn(scrubTurn)
            setScrubTurn(null)
          }
        }}
        onPointerCancel={() => setScrubTurn(null)}
      >
        <span className="bt-progress__title">replay scroll</span>
        <span className="bt-progress__count">
          turn {shownTurn.toString().padStart(2, '0')} / {progress.totalTurns.toString().padStart(2, '0')}
        </span>
        <div className="bt-progress__track" />
        <div className="bt-progress__fill" style={{ width: `${pct}%` }} />
        {ticks.map((p, i) => (
          <div
            key={i}
            className={`bt-progress__tick${p <= pct ? ' bt-progress__tick--passed' : ''}`}
            style={{ left: `${p}%` }}
          />
        ))}
        <div className="bt-progress__toggle" style={{ left: `${pct}%` }} />
      </div>

      <div className="bt-console__group">
        {progress.isFinished && (
          <button
            type="button"
            className="bt-console__primary bt-console__primary--mint"
            onClick={controls.restart}
            title="watch again"
          >
            ↺ replay
          </button>
        )}
        <button
          type="button"
          className="bt-console__primary"
          onClick={controls.skipToEnd}
          disabled={progress.isFinished}
          title="skip to end"
        >
          skip ⟶
        </button>
      </div>
    </section>
  )
}

/* ============================================================
 * 6. Winner overlay
 * ============================================================ */

function WinnerOverlay({
  winner,
  onRestart,
}: {
  winner: 'A' | 'B' | 'tie'
  onRestart: () => void
}): ReactElement {
  const meta = useMeta()
  const kanji = winner === 'A' ? '勝' : winner === 'B' ? '敗' : '引'
  const label =
    winner === 'tie' ? 'a tie!' :
    winner === 'A'   ? `${meta.playerA.name} wins!` :
                       `${meta.playerB.name} wins!`
  return (
    <div className="bt-winner" role="dialog" aria-modal="true">
      <div className="bt-winner__card">
        <span className="bt-winner__seal">battle end</span>
        <div className="bt-winner__kanji">{kanji}</div>
        <div className={`bt-winner__title bt-winner__title--${winner}`}>{label}</div>
        <button
          type="button"
          className="cute-btn mint"
          onClick={onRestart}
          style={{ fontSize: 16, padding: '12px 28px' }}
        >
          ↺ watch again
        </button>
      </div>
    </div>
  )
}

/* ============================================================
 * Mobile layout — landscape battlefield + rotate prompt
 * ============================================================
 *
 * Phones are short on vertical real estate, so the layout requires
 * landscape orientation to fit the kakejiku-style side-by-side
 * battlefield (similar to the desktop stage). The structure:
 *
 *   [ compact header strip                            ]
 *   [ Pill A | Stage(teamA rail | teamB rail) | PillB ]
 *   [ compact chronicle                               ]
 *   [ touch replay console                            ]
 *
 * In portrait the layout is hidden and a `<RotatePrompt>` overlay
 * asks the user to rotate. Browsers can't truly force landscape
 * on a regular web page (iOS refuses screen.orientation.lock,
 * Android requires fullscreen), so the prompt is the honest UX.
 *
 * All animated unit visuals (.bt-unit + its visualState classes)
 * are reused from the desktop tree; mobile-specific CSS shrinks
 * the wrapper / glyph / readout dimensions inside .bt-m-stage__rail.
 * VFX positioning uses `mobileSlot` since slotLeftPct is calibrated
 * for the desktop rail geometry (84px units, 10px gap).
 */

export function MobileLayout({
  state,
  progress,
  controls,
  onExit,
  stage,
}: {
  state: BattleDisplayState
  progress: ReplayProgress
  controls: ReplayControls
  onExit?: () => void
  /** Alternate battlefield renderer (PixiBattleScreen substitutes its
   *  WebGL stage); defaults to the DOM MobileStage. */
  stage?: React.ReactNode
}): ReactElement {
  // Both branches render — CSS toggles visibility on
  // (orientation: portrait) vs (orientation: landscape) so that
  // rotation immediately swaps which is visible without React
  // re-rendering the whole tree.
  return (
    <>
      <RotatePrompt />
      <div className="bt-m bt-m--landscape">
        <MobileHeader progress={progress} onExit={onExit} />
        <div className="bt-m-deck">
          <MobilePill side="A" state={state} />
          {stage ?? <MobileStage state={state} />}
          <MobilePill side="B" state={state} />
        </div>
        {/* Console sits directly under the deck so the play/skip
            controls land where the thumb expects them after watching.
            The chronicle is below; scroll down to read history. */}
        <MobileConsole progress={progress} controls={controls} />
        <MobileChronicle entries={state.log} state={state} />
      </div>
    </>
  )
}

function MobileHeader({
  progress,
  onExit,
}: {
  progress: ReplayProgress
  onExit?: () => void
}): ReactElement {
  const meta = useMeta()
  return (
    <header className="bt-m-header">
      {onExit && (
        <button
          type="button"
          className="bt-m-header__exit"
          onClick={onExit}
          aria-label="Return to shop"
        >
          ↩
        </button>
      )}
      <span className="bt-m-header__wordmark">Origami Battle</span>
      <span className="bt-m-header__title">
        {meta.playerA.name} vs. {meta.playerB.name}
      </span>
      <span className="bt-m-header__meta">
        R{meta.round.toString().padStart(2, '0')} · turn {progress.currentTurn}/{progress.totalTurns}
      </span>
    </header>
  )
}

function MobilePill({ side, state }: { side: Side; state: BattleDisplayState }): ReactElement {
  const meta = useMeta()
  const player = side === 'A' ? meta.playerA : meta.playerB
  const synergies = state.teams[side].synergies
  const Mascot = GLYPHS[player.mascot]!
  const hpPct = (player.hp / player.maxHp) * 100
  const low = hpPct < 33
  return (
    <aside className={`bt-m-pill bt-m-pill--${side}`} aria-label={`${player.name} status`}>
      <div className="bt-m-pill__mascot">
        <Mascot size={28} />
      </div>
      <div className="bt-m-pill__name">
        <div className="bt-m-pill__name-row">
          <span className="bt-m-pill__name-text">{player.name}</span>
          <span className="bt-m-pill__record">{player.record}</span>
        </div>
        <div className="bt-m-pill__hp" aria-label="player HP">
          <span
            className={`bt-m-pill__hp-fill${low ? ' bt-m-pill__hp-fill--low' : ''}`}
            style={{ width: `${hpPct}%` }}
          />
          <span className="bt-m-pill__hp-readout">♥ {player.hp}/{player.maxHp}</span>
        </div>
      </div>
      <div className="bt-m-pill__seals" aria-label="active synergies">
        {synergies.length === 0 && <span className="bt-m-pill__seals-empty">—</span>}
        {synergies.map((s) => (
          <span
            key={`${s.key}-${s.threshold}`}
            className="bt-m-pill__seal synergy-flash"
            title={s.description}
          >
            {s.key}×{s.threshold}
          </span>
        ))}
      </div>
    </aside>
  )
}

function MobileStage({ state }: { state: BattleDisplayState }): ReactElement {
  // Side-by-side rails, same as desktop Stage:
  //   Team A: front (lowest displayPosition) on the RIGHT of its
  //     rail (closest to the midline). Reverse the array + use
  //     justify-content: flex-end so the front renders last and the
  //     cluster packs to the right.
  //   Team B: front on the LEFT of its rail (closest to the midline).
  //     Ascending order + justify-content: flex-start.
  const meta = useMeta()
  const teamA = sortByDisplay(state.order.A.map((r) => state.units[r]).filter(Boolean) as UnitDisplayState[]).reverse()
  const teamB = sortByDisplay(state.order.B.map((r) => state.units[r]).filter(Boolean) as UnitDisplayState[])
  return (
    <div className="bt-m-stage">
      <span className="bt-m-stage__label bt-m-stage__label--A">
        {meta.playerA.name}{meta.viewerSide === 'A' ? ' · you' : ''}
      </span>
      <span className="bt-m-stage__label bt-m-stage__label--B">
        {meta.playerB.name}{meta.viewerSide === 'B' ? ' · you' : ''}
      </span>
      <div className="bt-m-stage__valley" aria-hidden="true" />

      <div className="bt-m-stage__rail bt-m-stage__rail--A">
        {teamA.map((u) => <BattleUnit key={u.ref} unit={u} side="A" compact />)}
      </div>
      <div className="bt-m-stage__rail bt-m-stage__rail--B">
        {teamB.map((u) => <BattleUnit key={u.ref} unit={u} side="B" compact />)}
      </div>

      <MobileFloatLayer floats={state.floatingNumbers} units={state.units} order={state.order} />
      <MobileBurstLayer bursts={state.impactBursts} units={state.units} order={state.order} />

      {state.finished && state.winner && <WinnerBanner winner={state.winner} />}
    </div>
  )
}

/** Mobile slot positioning — mirrors slotLeftPct's desktop geometry
 *  but tuned for the mobile rail sizing (47% rails on a stage that's
 *  typically ~540–720px wide, with 40px unit wrappers and 4px gaps).
 *  Front unit center sits ~6.5% inside the midline; each step back
 *  adds ~6.3%. Returns left% across the stage and a fixed top of
 *  50% (units are vertically centered in their rails). */
function mobileSlot(
  units: Record<UnitRef, UnitDisplayState>,
  order: { A: UnitRef[]; B: UnitRef[] },
  anchor: UnitRef,
): { left: number; top: number } | null {
  const u = units[anchor]
  if (!u) return null
  const sideRefs = order[u.team]
  const siblings = sideRefs
    .map((r) => units[r])
    .filter((s): s is UnitDisplayState => Boolean(s && s.visualState !== 'gone'))
    .sort((a, b) => a.displayPosition - b.displayPosition)
  const idx = siblings.findIndex((s) => s.ref === anchor)
  if (idx < 0) return null
  const slotPct = 0.065 + idx * 0.063
  const left = u.team === 'A' ? (0.5 - slotPct) * 100 : (0.5 + slotPct) * 100
  // Rail now sits at top:48% bottom:4px → units' glyph tops live
  // around 60–65% of stage height. Float/burst spawns there so the
  // damage numbers rise out of the unit rather than from mid-stage.
  return { left, top: 65 }
}

function MobileFloatLayer({
  floats,
  units,
  order,
}: {
  floats: FloatingNumber[]
  units: Record<UnitRef, UnitDisplayState>
  order: { A: UnitRef[]; B: UnitRef[] }
}): ReactElement {
  return (
    <>
      {floats.map((fn) => {
        const slot = mobileSlot(units, order, fn.anchor)
        if (!slot) return null
        const color =
          fn.variant === 'damage-up'   ? 'var(--vermillion-deep)' :
          fn.variant === 'damage'      ? 'var(--vermillion)' :
          fn.variant === 'damage-down' ? 'var(--ink-fade)' :
          fn.variant === 'heal'        ? 'var(--mint-deep)' :
          fn.variant === 'buff'        ? 'var(--gold-deep)' :
          fn.variant === 'debuff'      ? 'var(--lavender-deep)' :
          'var(--ink)'
        return (
          <div
            key={fn.id}
            className="float-number"
            style={{
              left: `${slot.left}%`,
              top: `${slot.top}%`,
              fontFamily: 'var(--pixel)',
              fontSize: fn.variant.startsWith('damage') ? 26 : 20,
              color,
              WebkitTextStroke: '1.5px var(--ink)',
              textShadow: '0 2px 0 var(--ink)',
              fontWeight: 400,
              letterSpacing: '0.04em',
              lineHeight: 1,
            }}
          >
            {fn.icon && <span style={{ fontSize: '0.7em', marginRight: 3 }}>{fn.icon}</span>}
            {fn.text}
          </div>
        )
      })}
    </>
  )
}

function MobileBurstLayer({
  bursts,
  units,
  order,
}: {
  bursts: ImpactBurst[]
  units: Record<UnitRef, UnitDisplayState>
  order: { A: UnitRef[]; B: UnitRef[] }
}): ReactElement {
  const meta = useMeta()
  return (
    <>
      {bursts.map((b) => {
        // Synergy banner — midpoint, above the unit row.
        if (b.kind === 'synergy_tag') {
          const teamColor =
            b.team === 'A' ? 'rgb(38, 78, 124)' :
            b.team === 'B' ? 'rgb(196, 64, 48)' :
            'var(--ink)'
          return (
            <div
              key={b.id}
              className="synergy-tag"
              style={{ left: '50%', top: '46%', ['--syn-tag-color' as unknown as keyof CSSProperties]: teamColor } as CSSProperties}
            >
              {b.kanji && <span className="synergy-tag__kanji">{b.kanji}</span>}
              {b.label}
            </div>
          )
        }
        // Projectile — animate source slot → target slot horizontally.
        if (b.kind === 'projectile') {
          const fromSlot = b.source ? mobileSlot(units, order, b.source) : null
          const toSlot = b.anchor !== 'midpoint' ? mobileSlot(units, order, b.anchor) : null
          if (!fromSlot || !toSlot) return null
          const rgb = resolveProjRgb(units, meta, b.source)
          return (
            <motion.div
              key={b.id}
              className="projectile"
              initial={{ left: `${fromSlot.left}%`, top: `${fromSlot.top}%` }}
              animate={{ left: `${toSlot.left}%`, top: `${toSlot.top}%` }}
              transition={{ duration: 0.34, ease: 'easeInOut' }}
              style={{ ['--proj-rgb' as unknown as keyof CSSProperties]: rgb } as CSSProperties}
            />
          )
        }
        if (b.kind === 'ability_impact') {
          if (b.anchor === 'midpoint') return null
          const slot = mobileSlot(units, order, b.anchor)
          if (!slot) return null
          const rgb = resolveProjRgb(units, meta, b.source)
          return (
            <div
              key={b.id}
              className="ability-impact"
              style={{ left: `${slot.left}%`, top: `${slot.top}%`, ['--proj-rgb' as unknown as keyof CSSProperties]: rgb } as CSSProperties}
            />
          )
        }
        if (b.anchor === 'midpoint') {
          // Match mobileSlot's unit-row Y so the midpoint clash lands
          // between the two front units along the valley line.
          return <div key={b.id} className="clash-burst" style={{ left: '50%', top: '70%' }} />
        }
        const slot = mobileSlot(units, order, b.anchor)
        if (!slot) return null
        const className = b.kind === 'super' ? 'super-burst' : 'resist-burst'
        return <div key={b.id} className={className} style={{ left: `${slot.left}%`, top: `${slot.top}%` }} />
      })}
    </>
  )
}

function MobileChronicle({
  entries,
  state,
}: {
  entries: LogEntry[]
  state: BattleDisplayState
}): ReactElement {
  const meta = useMeta()
  const [open, setOpen] = useState(false)
  // Cap the tray list at 12 entries so the dock doesn't grow
  // unbounded on long battles; the open tray scrolls internally if
  // it overflows the 60vh height.
  const recent = entries.slice(0, 12)
  const latest = entries[0]
  return (
    <section
      className={`bt-m-chronicle${open ? ' bt-m-chronicle--open' : ''}`}
      aria-label="battle chronicle"
    >
      <button
        type="button"
        className="bt-m-chronicle__handle"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-controls="bt-m-chronicle-tray"
      >
        <span className="bt-m-chronicle__caption-kanji">戦記</span>
        {!open && (
          latest ? (
            <span className="bt-m-chronicle__peek">
              <span className="bt-m-chronicle__entry-icon" style={{ color: latest.accent }}>{latest.icon}</span>
              <span className="bt-m-chronicle__entry-text" style={{ color: latest.accent }}>
                {renderLogText(latest.text, state, meta.unitNames)}
              </span>
            </span>
          ) : (
            <span className="bt-m-chronicle__peek bt-m-chronicle__peek--empty">battle starting…</span>
          )
        )}
        <span className="bt-m-chronicle__chevron" aria-hidden="true">{open ? '▾' : '▴'}</span>
      </button>
      <div
        className="bt-m-chronicle__tray"
        id="bt-m-chronicle-tray"
        role="log"
        aria-live="polite"
      >
        {recent.length === 0 ? (
          <span className="bt-m-chronicle__empty">battle starting…</span>
        ) : recent.map((e) => (
          <article key={e.id} className="bt-m-chronicle__entry">
            <span className="bt-m-chronicle__entry-turn">T{String(e.turn).padStart(2, '0')}</span>
            <span className="bt-m-chronicle__entry-icon" style={{ color: e.accent }}>{e.icon}</span>
            <span className="bt-m-chronicle__entry-text" style={{ color: e.accent }}>
              {renderLogText(e.text, state, meta.unitNames)}
            </span>
          </article>
        ))}
      </div>
    </section>
  )
}

function MobileConsole({
  progress,
  controls,
}: {
  progress: ReplayProgress
  controls: ReplayControls
}): ReactElement {
  // Same seek/scrub affordances as the desktop LacquerConsole —
  // drag or tap the progress strip to seek; the synergy intro
  // advances the fill via introFraction.
  const stripRef = useRef<HTMLDivElement>(null)
  const [scrubTurn, setScrubTurn] = useState<number | null>(null)

  function turnFromPointer(e: React.PointerEvent): number {
    const el = stripRef.current
    if (!el || progress.totalTurns <= 0) return 0
    const r = el.getBoundingClientRect()
    const f = Math.max(0, Math.min(1, (e.clientX - r.left) / Math.max(1, r.width)))
    return Math.round(f * progress.totalTurns)
  }

  const playheadTurn =
    scrubTurn ??
    (progress.currentTurn === 0 ? progress.introFraction : progress.currentTurn)
  const pct = progress.totalTurns > 0
    ? Math.min(100, (playheadTurn / progress.totalTurns) * 100)
    : 0
  return (
    <section className="bt-m-console" aria-label="replay controls">
      <div
        ref={stripRef}
        className={`bt-m-console__progress${scrubTurn !== null ? ' bt-m-console__progress--scrubbing' : ''}`}
        role="slider"
        aria-label="turn progress — drag to seek"
        aria-valuemin={0}
        aria-valuemax={progress.totalTurns}
        aria-valuenow={scrubTurn ?? progress.currentTurn}
        onPointerDown={(e) => {
          try {
            e.currentTarget.setPointerCapture(e.pointerId)
          } catch {
            /* uncapturable synthetic pointer — drag still works */
          }
          setScrubTurn(turnFromPointer(e))
        }}
        onPointerMove={(e) => {
          if (scrubTurn !== null) setScrubTurn(turnFromPointer(e))
        }}
        onPointerUp={() => {
          if (scrubTurn !== null) {
            controls.seekToTurn(scrubTurn)
            setScrubTurn(null)
          }
        }}
        onPointerCancel={() => setScrubTurn(null)}
      >
        <span className="bt-m-console__progress-fill" style={{ width: `${pct}%` }} />
      </div>
      <div className="bt-m-console__row">
        <button
          type="button"
          className="bt-m-btn"
          onClick={controls.stepBack}
          aria-label="rewind one turn"
        >
          ⏮
        </button>
        <button
          type="button"
          className={`bt-m-btn bt-m-btn--play${progress.isPlaying ? ' bt-m-btn--active' : ''}`}
          onClick={controls.toggle}
          aria-label={progress.isPlaying ? 'pause' : 'play'}
        >
          {progress.isPlaying ? '❚❚' : '▶'}
        </button>
        <div className="bt-m-console__speeds" role="group" aria-label="playback speed">
          {([0.5, 1, 2, 4] as const).map((s) => (
            <button
              key={s}
              type="button"
              className={`bt-m-btn bt-m-btn--speed${progress.speed === s ? ' bt-m-btn--active' : ''}`}
              onClick={() => controls.setSpeed(s)}
              aria-pressed={progress.speed === s}
              aria-label={`${s} times speed`}
            >
              {s === 0.5 ? '½×' : `${s}×`}
            </button>
          ))}
        </div>
        {progress.isFinished ? (
          <button
            type="button"
            className="bt-m-btn bt-m-btn--skip"
            onClick={controls.restart}
            aria-label="watch again"
          >
            ↺
          </button>
        ) : (
          <button
            type="button"
            className="bt-m-btn bt-m-btn--skip"
            onClick={controls.skipToEnd}
            aria-label="skip to end"
          >
            ⏭
          </button>
        )}
      </div>
    </section>
  )
}

/* ============================================================
 * Fold layout — Galaxy Fold inner-display (unfolded landscape)
 * ============================================================
 *
 * ~828×690 CSS px, hinge HORIZONTAL across y=50%. Forked at JSX
 * level via useIsFoldOpen() — same pattern as the Shop's FoldShop
 * and the same `.bt-fold-*` CSS namespace convention. Top half
 * reuses MobileHeader / MobilePill / MobileStage so all the battle
 * animations + replay scheduler work unchanged; bottom half is new
 * (FoldConsole + always-open FoldChronicle).
 */

export function FoldLayout({
  state,
  progress,
  controls,
  onExit,
  stage,
}: {
  state: BattleDisplayState
  progress: ReplayProgress
  controls: ReplayControls
  onExit?: () => void
  /** Alternate battlefield renderer — see MobileLayout. */
  stage?: React.ReactNode
}): ReactElement {
  return (
    <div className="bt-fold-page">
      <section className="bt-fold-top">
        <MobileHeader progress={progress} onExit={onExit} />
        <div className="bt-fold-deck">
          <MobilePill side="A" state={state} />
          {stage ?? <MobileStage state={state} />}
          <MobilePill side="B" state={state} />
        </div>
      </section>
      {/* Decorative crease line, identical recipe to .sh-fold-hinge.
          Pointer-events: none so it can't intercept clicks. */}
      <div className="bt-fold-hinge" aria-hidden />
      {/* Reuse the desktop replay console + chronicle directly —
          they paint the same lacquer console + indigo-capped scroll
          that the desktop tree shows, scaled down via .bt-fold-page
          descendant overrides in battle-screen.css so they fit the
          ~270px bottom-half budget. */}
      <section className="bt-fold-bottom">
        <LacquerConsole progress={progress} controls={controls} />
        <Chronicle entries={state.log} state={state} />
      </section>
    </div>
  )
}

export default BattleScreen
